import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faPlusSquare,
} from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';

import { ApiServiceServerless } from '../../../xhr_libs';

import {
  fmtBoolExists,
  fmtLongString,
} from '../../../components/tables/formatters';
import {
  fmtPlugCredentialStatus,
  fmtPlugCredentialisActive,
} from '../../../components/tables/formatters/plug';
import { PlugDownloadBill } from '../PlugDownloadBill';
import { PlugBulkDownloadbyAccount } from '../PlugBulkDownloadByAccount';

const styles = {
  buttonContainer: { width: '60%', textAlign: 'left', margin: 'auto' },
  buttonIcon: { marginRight: '.3rem', padding: '0px' },
};

export const getPlugCredentials = (
  setAllCredentials,
  setCredentialIds,
  organizationId,
  orgs,
  hiddenCredentialIds,
  setIsLoading,
  setAccountsOveride,
  showToast
) => {
  setIsLoading(true);
  const payload = { orgs: orgs };
  ApiServiceServerless.post(
    `/plug/credentials/get/${organizationId}`,
    payload,
    {
      authorization_id: organizationId,
    }
  )
    .then(({ data }) => {
      const creds = data.map((cred) => ({
        id: cred.id,
        credentialId: cred.id,
        correlationId: cred.correlationId,
        status: {
          credentialStatus: cred.status,
          credentialStatusDetail: cred.statusDetail,
        },
        utilityProvider: cred.provider.name,
        username: cred.username,
        hidden: hiddenCredentialIds.includes(cred.id),
        isActive: cred.isActive,
        shared: cred.share,
        psuedo: false,
      }));
      setAllCredentials(creds);
      setCredentialIds(creds.map((cred) => cred.credentialId));
      if (creds.length === 0) {
        setAccountsOveride(1)
      }
      setIsLoading(false);
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      setIsLoading(false);
      throw error;
    });
};

export const PLUG_CREDENTIAL_COLUMNS = [
  {
    dataField: 'utilityProvider',
    text: 'Utility Provider',
    formatter: fmtLongString,
  },
  {
    dataField: 'username',
    text: 'Associated Username',
    formatter: fmtLongString,
  },
  {
    dataField: 'status',
    text: 'Credential Status',
    formatter: fmtPlugCredentialStatus,
  },
  {
    dataField: 'isActive',
    text: 'Active',
    formatter: fmtPlugCredentialisActive,
  },
];

export const getPlugAccounts = (
  setPseudoCredentials,
  setProviderAccounts,
  organizationId,
  credentialIds,
  showToast,
  setIsLoading
) => {
  setIsLoading(true);
  ApiServiceServerless.post(
    `/plug/accounts`,
    {
      credentials: credentialIds,
      correlationId: organizationId,
    },
    { authorization_id: organizationId }
  )
    .then(({ data }) => {
      setProviderAccounts(handleParseAccounts(data.accounts, organizationId));
      setPseudoCredentials(data.pseudoCredentials);
      setIsLoading(false);
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      setIsLoading(false);
      throw error;
    });
};

const handleParseAccounts = (accounts, organizationId) => {
  let accounts_list = [];
  accounts.forEach((account) => {
    // check created at to make sure not in 5 minute delay for account deactivation. current backend timeout 5min current frontend timeout 10min
    let createdAt = dayjs.utc(account.createdAt);
    if ((dayjs.utc(new Date()) - createdAt) / 60 / 1000 > 10) {
      accounts_list.push({ ...parseAccount(account, organizationId) });
    }
  });
  return accounts_list;
};

const parseAccount = (account, organizationId) => {
  return {
    accountNumber: account.accountNumber,
    id: account.id,
    credentialId: account.credentialId,
    organizationId: organizationId,
    isStatementsProductActive: account.isStatementsProductActive,
    latestStatementDate: account.latestStatementDate,
    providerClassification: parseProviderClassification(
      account.providerClassification
    ),
    utilityProvider: account.provider.name,
    status: account.statusDetail,
    statusDetail: account.statusDetail,
    accountType: account.type,
    recent_url: `${process.env.REACT_APP_API_ENDPOINT_SERVERLESS}/plug/get_recent_url/${account.id}`,
    hideAccount: account.customData && account.customData.hideAccount && account.customData.hideAccount === 'true' ? true : false
  };
};

const parseProviderClassification = (providerClassification) => {
  if (providerClassification === 'PUBLISHER') {
    return 'PRIMARY';
  }
  if (providerClassification === 'PASS_THROUGH') {
    return 'SECONDARY';
  }
  return '';
};

export const joinPlugAccountsToLocal = (
  providerAccounts,
  localUtilityAccounts,
  setProviderAccountsWithLocal
) => {
  const all_accounts = providerAccounts.map((acct) => ({
    ...acct,
    localAccount: localUtilityAccounts.find(
      (local) => local.plug_account_id === acct.id
    ),
  }));
  setProviderAccountsWithLocal(all_accounts);
};

export const updatePlugUsername = (
  params,
  credential,
  updateCredentialState,
  setAllCredentials,
  showToast
) => {
  ApiServiceServerless.post('/plug/credentials/update', params, {
    authorization_id: params.org_id,
  })
    .then(() => {
      updateCredentialState(
        setAllCredentials,
        credential.credentialId,
        'username',
        params.new_username
      );
      showToast('success', 'Success', 'Username was updated');
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      throw error;
    });
};

export const updatePlugPassword = (params, showToast) => {
  ApiServiceServerless.post('/plug/credentials/update', params, {
    authorization_id: params.org_id,
  })
    .then(() => {
      showToast('success', 'Success', 'Password was updated');
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      throw error;
    });
};

export const updatePlugAccount = (
  modalData,
  organizationId,
  data,
  newAccountId,
  setProviderAccounts,
  setLocalUtilityAccounts,
  setSubmitDisabled,
  showToast,
  onHide,
  setIsSaving
) => {
  let payload = {
    plugAccount: modalData.id,
    org_id: organizationId,
    credentialId: modalData.credentialId,
  };
  const local_account_id = modalData.localAccount
    ? modalData.localAccount.id
    : '';
  if (local_account_id.toString() !== newAccountId.toString()) {
    payload.linkedAccount = newAccountId;
  }
  if (modalData.isStatementsProductActive !== data.active) {
    payload.active = data.active;
  }

  ApiServiceServerless.post(`/plug/accounts/update`, payload, {
    authorization_id: organizationId,
  })
    .then((res) => {
      handleAccountReturn(
        res,
        modalData.id,
        payload.linkedAccount,
        'active' in payload
          ? payload.active
          : modalData.isStatementsProductActive,
        setProviderAccounts,
        setLocalUtilityAccounts
      );
      showToast(
        'success',
        'Success',
        'Account ' + modalData.accountNumber + ' updated.'
      );
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      throw error;
    })
    .finally(() => {
      onHide();
      setSubmitDisabled(false);
      setIsSaving(false);
    });
};

export const unlinkPlugAccount = (
  modalData,
  organizationId,
  setProviderAccounts,
  setLocalUtilityAccounts,
  setSubmitDisabled,
  showToast,
  onHide,
  setIsSaving
) => {
  setIsSaving(true)

  ApiServiceServerless.post(`/plug/accounts/unlink/${modalData.id}`, {}, {
    authorization_id: organizationId,
  })
    .then((res) => {
      setProviderAccounts((prev) =>
        prev.map((account) => {
          if (account.id !== modalData.id) return account;
          let editted_account = { ...account };
          editted_account.isStatementsProductActive = res.data.plug_status.active;
          return editted_account;
        })
      );
      setLocalUtilityAccounts((prev) =>
        prev.map((account) => {
          if (account.id !== parseInt(res.data.utility_account.id)) return account;
          return res.data.utility_account;
        })
      );

      showToast(
        'success',
        'Success',
        'Account ' + modalData.accountNumber + ' unlinked.'
      );
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      throw error;
    })
    .finally(() => {
      onHide();
      setSubmitDisabled(false);
      setIsSaving(false);
    });
};

export const updatePlugHidden = (modalData, value, organizationId, setProviderAccounts, setSubmitDisabled, showToast,
  onHide,
  setIsSaving) => {
  setIsSaving(true)
  let payload = {
    plugAccount: modalData.id,
    org_id: organizationId,
    credentialId: modalData.credentialId,
    'customData': { 'hideAccount': value === true ? 'true' : false }
  };

  ApiServiceServerless.post(`/plug/accounts/update`, payload, {
    authorization_id: organizationId,
  })
    .then((res) => {
      setProviderAccounts((prev) =>
        prev.map((account) => {
          if (account.id !== modalData.id) return account;
          let editted_account = { ...account };
          editted_account.hideAccount = value
          return editted_account;
        })
      );
      showToast(
        'success',
        'Success',
        'Account ' + modalData.accountNumber + ' updated.'
      );
    })
    .catch((error) => {
      showToast('danger', 'Error', error);
      throw error;
    })
    .finally(() => {
      onHide();
      setSubmitDisabled(false);
      setIsSaving(false);
    });

}

const handleAccountReturn = (
  res,
  provider_account_id,
  linkedAccount,
  active_status,
  setProviderAccounts,
  setLocalUtilityAccounts
) => {
  if (res.data.plug_status) {
    setProviderAccounts((prev) =>
      prev.map((account) => {
        if (account.id !== provider_account_id) return account;
        let editted_account = { ...account };
        if (res.data.plug_status.active) {
          editted_account.isStatementsProductActive = active_status;
        }
        return editted_account;
      })
    );
  }
  if (res.data.utility_account) {
    //maybe set new plug account id
    setLocalUtilityAccounts((prev) =>
      prev.map((account) => {
        if (
          account.plug_account_id !== res.data.utility_account.plug_account_id
        )
          return account;
        let new_account = { ...account };
        new_account.plug_account_id = null;
        return new_account;
      })
    );
    //maybe set new account
    setLocalUtilityAccounts((prev) =>
      prev.map((account) => {
        if (account.id !== parseInt(linkedAccount)) return account;
        return res.data.utility_account;
      })
    );
  }
};


export const getPlugAccountColumns = (handleOpenLinkModal, updateAccess) => {
  let plug_account_columns = [
    {
      dataField: 'accountNumber',
      text: 'Account Number',
      formatter: (_cell, row) => {
        return (
          <div>
            {row.accountNumber}

            <div style={{ display: 'inline-block', paddingLeft: '5px' }}>
              <PlugBulkDownloadbyAccount
                plug_account_id={row.id}
                accountNumber={row.accountNumber}
                credential_id={row.credentialId}
                utilityProvider={row.utilityProvider}
                organizationId={row.organizationId}
                style={{
                  fontSize: '18px',
                  marginLeft: '8px',
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      dataField: 'latestStatementDate',
      text: 'Latest Bill Date',
      formatter: (_cell, row) => {
        return (
          <div>
            {row.latestStatementDate}
            {row.latestStatementDate &&
              row.latestStatementDate !== '' &&
              row.recent_url && (
                <div style={{ display: 'inline-block', paddingLeft: '5px' }}>
                  <PlugDownloadBill
                    plug_url={row.recent_url}
                    organization_id={row.organizationId}
                    style={{
                      fontSize: '18px',
                    }}
                    account_string={row.accountNumber ? row.accountNumber : ''}
                    date_string={row.latestStatementDate ? row.latestStatementDate : ''}
                  />
                </div>
              )}
          </div>
        );
      },
    },
    {
      dataField: 'isStatementsProductActive',
      text: 'Active',
      formatter: fmtBoolExists,
    },
    {
      dataField: 'localAccount',
      text: 'Linked',
      formatter: fmtBoolExists,
    },
    {
      dataField: 'id',
      text: 'Settings',
      formatter: (_cell, row) => {
        return updateAccess ? (
          <Button
            size='sm'
            variant='link'
            style={{ width: '100%', margin: '0px', padding: '0px' }}
            onClick={() => handleOpenLinkModal(row, 2)}
          >
            <div style={styles.buttonContainer}>
              <FontAwesomeIcon
                icon={row.localAccount ? faEdit : faPlusSquare}
                style={styles.buttonIcon}
              />
              {'Edit'}
            </div>
          </Button>
        ) : (
          ''
        );
      },
    },
  ];
  return plug_account_columns;
};

// use show secondary as a way of also showing summary accounts
export const filterPlugAccountByCredential = (
  credential,
  account,
  showSecondary,
  showHidden
) => {
  return (
    account.credentialId === credential.credentialId &&
    (account.accountType !== 'SUMMARY' || showSecondary) && (account.hideAccount === false || showHidden)
  );
};

export const handleSetNewPlugAccount = (
  newAccount,
  setLocalUtilityAccounts
) => {
  setLocalUtilityAccounts((prev) =>
    prev.map((account) => {
      if (account.plug_account_id !== newAccount.plug_account_id) {
        return account;
      }
      let new_account = { ...account };
      new_account.plug_account_id = null;
      return new_account;
    })
  );
  setLocalUtilityAccounts((prev) => [...prev, newAccount]);
};

export const getPlugTemplates = (
  organizationId,
  setUtilityTemplates,
  setIsLoadingTemplates
) => {
  setIsLoadingTemplates(true);
  ApiServiceServerless.get(`/custom_templates/${organizationId}`, {
    authorization_id: organizationId,
  })
    .then(({ data }) => {
      setUtilityTemplates(data);
    })
    .finally(() => {
      setIsLoadingTemplates(false);
    });
};


export const getPlugMeterGroups = (
  organizationId,
  setMeterGroups,
  setIsLoadingMeterGroups
) => {
  setIsLoadingMeterGroups(true);
  ApiServiceServerless.get(`/plug_meter_groups/${organizationId}`, {
    authorization_id: organizationId,
  })
    .then(({ data }) => {
      setMeterGroups(data);
    })
    .finally(() => {
      setIsLoadingMeterGroups(false);
    });
};


export const getPlugMeterNames = (
  meterData, accountData
) => {
  let name = meterData["meterId"]
  if (meterData["meterNumber"]) { name = meterData["meterNumber"] }
  else if (meterData["pointOfDeliveryNumber"]) {
    name = meterData["pointOfDeliveryNumber"]
  }
  else if (meterData["generalDescriptionAsPrinted"]) {
    name = meterData["generalDescriptionAsPrinted"]
  }
  else if (accountData['accountNumber']) {
    name = accountData['accountNumber']
  }
  return name
}